import { getLogger } from "@expert/logging";
import { useDebouncedValue } from "@mantine/hooks";
import { useState } from "react";
import { useAnalytics } from "@/analytics";
import { useVerifiedCustomerStore } from "@/customer-info/store";
import { useAddressSearch } from "@/sales-tools/home-product/hooks/useAddressSearch";
import { useCollectCustomerInfoForm } from "@/sales-tools/home-product/hooks/useCollectCustomerInfoForm";
import { getReservationSid } from "@/sales-tools/utils";
import { parsePhoneNumberAttributes } from "@/sales-tools/utils/parsePhoneNumberAttributes";
import { usePartner, useSessionId, useTypedMostRecentTask } from "@/sdk";
import type { Address } from "@/shared-types/address";
import { getEligibility } from "../api";
import { useProductOfferStore } from "../stores";
import { useIntegratedFlowStore } from "../stores/useIntegratedFlowStore";
import type { EligibilityRequest } from "../types";
import { EligibilityStatus } from "../types";

const logger = getLogger({ module: "useCollectCustomerInfo", supportTeam: "sales" });

export const useCollectCustomerInfo = () => {
    const { dispatcher } = useAnalytics();
    const {
        task: mostRecentTask,
        isVoice,
        voiceTask,
        isChat,
        chatTask,
        customerInteractionId,
    } = useTypedMostRecentTask();

    const partner = usePartner();
    const sessionId = useSessionId();

    const customer = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId).customer);
    const setCustomer = useVerifiedCustomerStore((state) => state.setCustomer);
    const integratedStep = useIntegratedFlowStore((state) => state.getCurrentStep(sessionId));
    const updateIntegratedStep = useIntegratedFlowStore((state) => state.updateIntegratedStep);
    const selectedProduct = useProductOfferStore((state) => state.getSelectedProduct(sessionId));

    if (!customer || !customer.mdn) {
        logger.error("Customer data is undefined");
        throw new Error("Customer data is undefined");
    }

    if (!isVoice && !isChat) {
        logger.error("Active task is not voice or chat task");
        throw new Error("Active task is not voice or chat task");
    }

    const form = useCollectCustomerInfoForm(customer);
    const [debouncedAddressSearch] = useDebouncedValue(form.values.addressSearch, 300);
    const { data: addressSuggestions = [], isLoading } = useAddressSearch(debouncedAddressSearch);

    const [eligibilityStatus, setEligibilityStatus] = useState<EligibilityStatus | null>(null);
    const [isLoadingEligibility, setIsLoadingEligibility] = useState(false);
    const [sentIneligibleAddress, setSentIneligibleAddress] = useState(false);

    const handleContinueToPayment = async () => {
        try {
            setIsLoadingEligibility(true);

            const address: Address = {
                Address1: form.values.address1,
                City: form.values.city,
                StateProvinceCode: form.values.state,
                PostalCode: form.values.zip,
                CountryCode: form.values.countryCode,
            };

            setCustomer(sessionId, {
                ...customer,
                firstName: form.values.firstName,
                lastName: form.values.lastName,
                fullName: `${form.values.firstName} ${form.values.lastName}`,
                email: form.values.email,
                address,
            });

            const request: EligibilityRequest = {
                asurionCallId: voiceTask?.asurionCallId,
                customerInteractionId,
                partner,
                reservationSid: getReservationSid(mostRecentTask) ?? mostRecentTask?.analyticsData?.ContactId,
                sessionId,
                ...parsePhoneNumberAttributes(customer?.mdn),
                address,
                customer: {
                    firstName: form.values.firstName,
                    lastName: form.values.lastName,
                    email: form.values.email,
                },
                eligibilityAttributes: {
                    currentPlan: customer?.productName,
                    isInternationalCaller: customer?.metadata?.isInternationalCaller,
                    chatSourceApp: isChat ? chatTask?.sourceApp : undefined,
                },
            };

            const eligibilityResult = await getEligibility(request);
            const { productConfigs } = eligibilityResult;
            const selectedEligibleProduct = productConfigs.find((p) => p.productSku === selectedProduct?.productSku);

            if (!selectedEligibleProduct) {
                const store = useProductOfferStore.getState();
                store.updateSelectedProduct(sessionId, undefined);

                updateIntegratedStep(sessionId, "SuggestedOfferStep");
            } else if (selectedEligibleProduct.status === EligibilityStatus.Eligible) {
                setEligibilityStatus(selectedEligibleProduct.status);

                const store = useProductOfferStore.getState();
                store.updateProducts(
                    sessionId,
                    productConfigs.filter((p) => p.isPrimaryProduct),
                );
                store.updateSelectedProduct(sessionId, selectedEligibleProduct);

                updateIntegratedStep(sessionId, "SendLinkStep");
            } else {
                if (!sentIneligibleAddress) {
                    setSentIneligibleAddress(true);
                    void dispatcher.dispatchBusinessEvent("FirstIneligibleAddressReceived");
                } else {
                    setEligibilityStatus(selectedEligibleProduct.status);
                    const store = useProductOfferStore.getState();
                    store.updateProducts(
                        sessionId,
                        productConfigs.filter((p) => p.isPrimaryProduct),
                    );
                    store.updateSelectedProduct(sessionId, selectedEligibleProduct);
                    void dispatcher.dispatchBusinessEvent("SecondIneligibleAddressReceived");
                }
            }

            void dispatcher
                .withExtra({ nextStep: integratedStep })
                .dispatchBusinessEvent("ButtonClicked_ContinueToPayment");
        } catch (error) {
            logger.error({ error }, "Error checking eligibility");
        } finally {
            setIsLoadingEligibility(false);
        }
    };

    return {
        addressSuggestions,
        customerMdn: customer.mdn,
        debouncedAddressSearch,
        eligibilityStatus,
        form,
        handleContinueToPayment,
        integratedStep,
        isLoading,
        isLoadingEligibility,
        product: selectedProduct,
        sentIneligibleAddress,
        setSentIneligibleAddress,
    };
};
